/**
 * Arc & Light — content constants
 * All copy is kept here so editing words doesn't require touching layout.
 */

export const NAV_LINKS = [
    { label: "Approach", href: "#approach" },
    { label: "Services", href: "#services" },
    { label: "Process", href: "#process" },
    { label: "Interiors", href: "#interiors" },
    { label: "Contact", href: "#contact" },
];

export const HERO = {
    eyebrow: "Interior Design Consultancy · Brisbane",
    headline: "We design the feeling of you.",
    subhead:
        "Arc & Light is a boutique interior design consultancy creating warm, considered, and deeply personal interiors. The studio blends practical design guidance with curated sourcing to shape homes that feel cohesive, elevated, and aligned with the way people truly live.",
    cta: "Book Your Discovery Consultation",
    supporting: "A calm first conversation about your home.\nNo pressure, no obligation.",
};

export const OUTCOMES = [
    "Cohesive across every room, not a collection of nice things",
    "Calm and grounding to come home to",
    "Personal — it feels like you, not a showroom",
    "Effortless to live in, considered in every detail",
    "Sourced with care from local makers and artisans",
];

export const SERVICES = [
    {
        title: "Interior Design Consultation",
        body: "Expert direction for your space, from first idea to finished room.",
    },
    {
        title: "Home Styling & Curation",
        body: "The final layer that makes a room feel complete.",
    },
    {
        title: "Furniture, Art & Decor",
        body: "Considered pieces, sourced and brought together as a whole.",
    },
    {
        title: "Colour Consultation",
        body: "Palettes that bring calm and cohesion across your home.",
    },
    {
        title: "Material & Finish Selection",
        body: "Tactile, lasting, beautifully matched.",
    },
    {
        title: "Local Product & Supplier Sourcing",
        body: "Access to trusted Brisbane makers, artisans and suppliers.",
    },
];

export const PROCESS = [
    {
        n: "01",
        title: "Discovery",
        body: "We begin with a relaxed conversation about your home, your life, and how you want each space to feel.",
    },
    {
        n: "02",
        title: "Direction",
        body: "Merilyn shapes a clear creative direction — palette, materials, mood and priorities — so the path forward feels obvious, not overwhelming.",
    },
    {
        n: "03",
        title: "Curation",
        body: "We source and bring together furnishings, art and finishes, drawing on trusted local makers and artisans.",
    },
    {
        n: "04",
        title: "Completion",
        body: "The final styling and considered details that make your home feel whole.",
    },
];

export const MERILYN_BIO = [
    "Merilyn Smith is the founder and lead consultant of Arc & Light, a boutique interior design consultancy based in Brisbane.",
    "With over 15 years of experience across photography, visual arts, creative direction, design, and styling, Merilyn brings a unique perspective to the spaces she creates. Her career has spanned international fashion, editorial photography, branding, visual storytelling, and fine art, giving her a highly developed eye for composition, colour, texture, light, and atmosphere.",
    "Coming from a family with a strong architectural heritage, Merilyn was immersed in the world of design from an early age through her father\u2019s role as Director of Architectus, one of Australia\u2019s leading architectural firms. These early experiences shaped her understanding of space, form, and the profound influence on our environments.",
    "Having worked across London and Paris Fashion Weeks, international documentary projects, published editorial campaigns, and creative businesses of her own, Merilyn understands how thoughtful design can influence the way people feel within a space. Her experience as an artist, photographer, designer, and creative director has refined her ability to see not only how a space looks, but how it feels.",
    "Through Arc & Light, she helps homeowners create warm, beautiful, and deeply personal interiors that reflect their lifestyle and values. Her approach combines practical design guidance with thoughtful curation, sourcing quality furnishings, materials, artwork, and decor from local suppliers, makers, and artisans wherever possible. Sustainability, craftsmanship, and supporting local businesses are central to her philosophy.",
    "Merilyn believes a home should be more than visually appealing. It should feel welcoming, harmonious, and supportive of everyday life. Her work focuses on creating spaces that foster connection, comfort, beauty, and a genuine sense of belonging.",
    "Known for her calm and collaborative approach, Merilyn works closely with clients to simplify decision making, bring clarity to the design process, and transform homes into spaces that feel both elevated and effortlessly lived in.",
];

export const MERILYN_QUOTE =
    "Arc & Light was founded on the belief that when a space feels aligned, life flows more beautifully within it.";

export const PORTRAIT_URL =
    "https://customer-assets.emergentagent.com/job_2c676e44-df72-41fd-9012-e2e5c58bf409/artifacts/vm1kpju1_Merilyn%20Smith%20Portrait.jpg";

export const HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_considered-home/artifacts/khe3snm8_Heavyweight%20Linen%20Bedcover%20-%20Sand%20%20%7C%20CULTIVER%20-%20%20Australia%20copy.jpg";

export const LEAD_MAGNET_IMAGE =
    "https://customer-assets.emergentagent.com/job_considered-home/artifacts/aq1pu62z_Esthe%CC%81tique%20des%20matie%CC%80res%20premie%CC%80res%20%7C%20Raw%20materials%20aesthetic.jpg";

export const MOOD_IMAGES = [
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/khe3snm8_Heavyweight%20Linen%20Bedcover%20-%20Sand%20%20%7C%20CULTIVER%20-%20%20Australia%20copy.jpg", alt: "Heavyweight linen bedcover, warm afternoon light", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/we6ip1oi_Crafted%20Silence.jpg", alt: "Crafted silence — a considered interior", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/1o63e8fv_Sink%20Into%20Style_%20Cozy%20Designer%20Sofas%20for%20Modern%20Living.jpg", alt: "A sculptural chocolate velvet sofa", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/aq1pu62z_Esthe%CC%81tique%20des%20matie%CC%80res%20premie%CC%80res%20%7C%20Raw%20materials%20aesthetic.jpg", alt: "Folded raw linen, an aesthetic of materials", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/t67w3orr_RD%20as%20captured%20by%20you%20%F0%9F%8C%8E.jpg", alt: "A calm room in soft natural light", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/72tb3ul1_Lina%20Paciello.jpg", alt: "A study in tone and material", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/i2h8647a_%20-3%20copy%205.jpg", alt: "Considered composition of light and surface", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/ds1g62p0_%20-4%20copy%206.jpg", alt: "Warmth and restraint", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/sepa5qgm_%20-7.jpg", alt: "Quiet, lived-in materiality", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/5nsxct1k_%D0%9D%D0%B0%D0%B1%D0%BE%D1%80%20%D1%82%D0%B0%D1%80%D0%B5%D0%BB%D0%BE%D0%BA%20%D0%BF%D1%80%D0%BE%D0%B4%D0%B0%D0%B5%D1%82%D1%81%D1%8F%20%D0%B2%20Sachet_space.jpg", alt: "A ribbed shell — natural form", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/972tr3kd_%20-3%20copy%206.jpg", alt: "Quiet form, soft daylight", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/eycz67at_%20-10.jpg", alt: "A still composition", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/jfz3bti8_%20-4%20copy%204.jpg", alt: "Tactile detail in warm tone", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/zh5ld798_%20-4%20copy%205.jpg", alt: "A moment of considered restraint", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/81aai6yw_%20-4%20copy%203.jpg", alt: "Quiet surface and form", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/1ujkk2ln_%20-5%20copy%203.jpg", alt: "Warm light and natural materiality", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/b3e07q2s_%20-6.jpg", alt: "A study in stillness", aspect: "aspect-[4/5]" },
    { url: "https://customer-assets.emergentagent.com/job_considered-home/artifacts/zozzc8b4_1.jpg", alt: "A considered detail", aspect: "aspect-[4/5]" },
];

export const TESTIMONIALS = [
    {
        quote:
            "Merilyn is a true artist and a beautiful soul, deeply intuitive and endlessly creative. The magic she weaves through her work is a testament to her gift of seeing beyond the surface.",
        name: "Trillian",
        suburb: "",
    },
    {
        quote:
            "She made the hardest decisions feel obvious. We trusted her completely and the result is a home that quietly holds us every day.",
        name: "Eleanor",
        suburb: "Hawthorne",
    },
    {
        quote:
            "I have had the pleasure of working with Merilyn professionally and always admire her creative process. Merilyn brought a soulful, authentic touch, and created an experience that felt warm and personal — the result genuinely reflects who we are. It means so much to us.",
        name: "Nicola",
        suburb: "New Farm",
    },
];

export const OFFER_INCLUDES = [
    "A relaxed in-home or virtual session with Merilyn",
    "A clear read on what's working, what's holding the room back, and what to prioritise first",
    "An initial direction for palette, materials and mood",
    "A simple, no-overwhelm roadmap for what's next",
    "Access to trusted local makers, artisans and suppliers",
];

export const CREDIBILITY = [
    "15+ years",
    "Fashion & fine-art eye",
    "Architectural heritage",
    "Brisbane-based",
];

// Public Web3Forms access key — safe to expose client-side.
// It only permits submissions to the email verified at web3forms.com (merilyn@merilyn.com.au).
export const WEB3FORMS_KEY = "73a56048-f185-4d35-9fa1-783550c7852f";

export const SUBURBS =
    "Norman Park · Bulimba · Hawthorne · Camp Hill · and surrounding suburbs";
